import React, { useState } from 'react';
import { Navigation, MapPin, X } from 'lucide-react';
import { useGPS } from '../../contexts/GPSContext';
import { Link } from 'react-router-dom';

export default function GPSStatusIndicator() {
  const { isTracking, currentLocation, lastUpdate, currentLoadId, stopTracking } = useGPS();
  const [showDetails, setShowDetails] = useState(false);

  if (!isTracking) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center space-x-2 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors text-sm font-medium"
      >
        <Navigation className="h-4 w-4 animate-pulse" />
        <span>GPS Active</span>
      </button>

      {showDetails && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDetails(false)}
          ></div>
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <Navigation className="h-4 w-4 mr-2 text-green-600 animate-pulse" />
                GPS Tracking Active
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              {currentLoadId ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-900 mb-1">
                    <MapPin className="h-3 w-3 inline mr-1" />
                    Load Tagged
                  </p>
                  <p className="text-xs text-blue-700">
                    Your location is being tracked for this load
                  </p>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-green-900 mb-1">
                    <MapPin className="h-3 w-3 inline mr-1" />
                    Available for Backhauls
                  </p>
                  <p className="text-xs text-green-700">
                    You're visible on the Active Drivers Map
                  </p>
                </div>
              )}

              {currentLocation && (
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Speed:</span>
                    <span className="font-medium text-gray-900">
                      {currentLocation.speed.toFixed(0)} mph
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Accuracy:</span>
                    <span className="font-medium text-gray-900">
                      ±{currentLocation.accuracy.toFixed(0)}m
                    </span>
                  </div>
                </div>
              )}

              {lastUpdate && (
                <div className="text-xs text-gray-600">
                  Last update: {lastUpdate.toLocaleTimeString()}
                </div>
              )}

              <div className="pt-3 border-t border-gray-200 space-y-2">
                <Link
                  to="/profile"
                  onClick={() => setShowDetails(false)}
                  className="block w-full px-3 py-2 text-center bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Manage GPS Settings
                </Link>
                <button
                  onClick={async () => {
                    await stopTracking();
                    setShowDetails(false);
                  }}
                  className="w-full px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Stop Tracking
                </button>
              </div>

              <p className="text-xs text-gray-500 pt-2">
                Updates every 5 minutes with minimal battery impact
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
